import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportField,
  ReportFlowPages,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';
import type { BoardLine } from '../replacementPlannerUtils';
import { crewName } from '../replacementPlannerUtils';
import type { CrewReplacementStatus, CrewVesselListResponse } from '../types';

function dash(v: React.ReactNode) {
  if (v == null || v === '') return '—';
  return v;
}

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

type StatusTone = 'needed' | 'candidate' | 'confirmed' | 'travel' | 'completed' | 'cancelled' | 'open';

function statusTone(status?: CrewReplacementStatus | null): StatusTone {
  switch (status) {
    case 'NEEDED':
      return 'needed';
    case 'CANDIDATE':
      return 'candidate';
    case 'CONFIRMED':
      return 'confirmed';
    case 'TRAVEL':
      return 'travel';
    case 'COMPLETED':
      return 'completed';
    case 'CANCELLED':
      return 'cancelled';
    default:
      return 'open';
  }
}

function statusLabel(status?: CrewReplacementStatus | null): string {
  if (!status) return 'Open';
  switch (status) {
    case 'NEEDED':
      return 'Needed';
    case 'CANDIDATE':
      return 'Candidate';
    case 'CONFIRMED':
      return 'Confirmed';
    case 'TRAVEL':
      return 'Travel';
    case 'COMPLETED':
      return 'Completed';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return status;
  }
}

function planSignOff(line: BoardLine): string {
  return fmtDate(
    line.replacement?.plannedSignOffDate ||
      line.onboard.coeDate ||
      line.onboard.intentionSignOffDate,
  );
}

function TableHead() {
  return (
    <table className="repl-plan-table repl-plan-table-head">
      <thead>
        <tr>
          <th className="repl-plan-col-no">No.</th>
          <th className="repl-plan-col-rank">Rank</th>
          <th className="repl-plan-col-name">Outgoing</th>
          <th className="repl-plan-col-name">Incoming</th>
          <th className="repl-plan-col-status">Status</th>
          <th className="repl-plan-col-date">Plan S/Off</th>
          <th className="repl-plan-col-date">Plan S/On</th>
          <th className="repl-plan-col-port">Port</th>
          <th className="repl-plan-col-notes">Notes</th>
        </tr>
      </thead>
    </table>
  );
}

function LineRow({ line, index }: { line: BoardLine; index: number }) {
  const tone = statusTone(line.replacement?.status);
  const incoming = line.replacement?.incomingCrewName;
  return (
    <table className={`repl-plan-table repl-plan-row-table repl-plan-row-${tone}`}>
      <tbody>
        <tr>
          <td className="repl-plan-col-no">{index + 1}</td>
          <td className="repl-plan-col-rank">{dash(line.rankLabel)}</td>
          <td className="repl-plan-col-name">
            {dash(crewName(line.onboard) || line.replacement?.outgoingCrewName)}
          </td>
          <td className={`repl-plan-col-name${!incoming ? ' repl-plan-incoming-tbd' : ''}`}>
            {incoming || 'TBD'}
          </td>
          <td className="repl-plan-col-status">
            <span className={`repl-plan-status repl-plan-status-${tone}`}>
              {statusLabel(line.replacement?.status)}
            </span>
          </td>
          <td className="repl-plan-col-date">{planSignOff(line)}</td>
          <td className="repl-plan-col-date">{fmtDate(line.replacement?.plannedSignOnDate)}</td>
          <td className="repl-plan-col-port">{dash(line.replacement?.changePort)}</td>
          <td className="repl-plan-col-notes">{dash(line.replacement?.notes)}</td>
        </tr>
      </tbody>
    </table>
  );
}

export type ReplacementPlanReportProps = {
  data: CrewVesselListResponse;
  lines: BoardLine[];
  withinDays: number;
  needsReliefOnly: boolean;
  companyLogoSrc?: string | null;
  companyName?: string | null;
};

export const ReplacementPlanReport: React.FC<ReplacementPlanReportProps> = ({
  data,
  lines,
  withinDays,
  needsReliefOnly,
  companyLogoSrc = null,
  companyName = null,
}) => {
  const vessel = data.vessel;
  const vesselName = vessel?.vesselName || '—';
  const flag = vessel?.flag?.name || vessel?.flag?.isoCode2 || '—';

  const counts = useMemo(() => {
    const base: Record<StatusTone, number> = {
      needed: 0,
      candidate: 0,
      confirmed: 0,
      travel: 0,
      completed: 0,
      cancelled: 0,
      open: 0,
    };
    for (const line of lines) {
      base[statusTone(line.replacement?.status)] += 1;
    }
    return base;
  }, [lines]);

  const firstHeader = useMemo(
    () => (
      <div className="repl-plan-flow-header">
        <div className="repl-plan-accent" aria-hidden />
        <ReportHeader>
          <ReportTitleRow
            title="Replacement plan"
            formId="Crew relief"
            logoSrc={companyLogoSrc}
            logoAlt={companyName || 'Management company logo'}
          />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Vessel" value={vesselName} />
              <ReportField label="IMO number" value={dash(vessel?.imoNumber)} />
              <ReportField label="Call sign" value={dash(vessel?.callSign)} />
              <ReportField label="Flag" value={flag} />
            </ReportMetaRow>
            <ReportMetaRow>
              <ReportField label="Within days" value={String(withinDays)} />
              <ReportField
                label="Rows"
                value={needsReliefOnly ? 'Needs relief' : 'All onboard'}
              />
              <ReportField label="Lines" value={String(lines.length)} />
              <ReportField label="Printed" value={fmtDate(new Date().toISOString().slice(0, 10))} />
            </ReportMetaRow>
          </ReportMeta>
          <div className="repl-plan-kpis">
            {(
              [
                ['needed', 'Needed', counts.needed],
                ['candidate', 'Candidate', counts.candidate],
                ['confirmed', 'Confirmed', counts.confirmed],
                ['travel', 'Travel', counts.travel],
                ['completed', 'Completed', counts.completed],
                ['open', 'Open', counts.open],
              ] as const
            ).map(([tone, label, value]) => (
              <div key={tone} className={`repl-plan-kpi repl-plan-kpi-${tone}`}>
                <span className="repl-plan-kpi-label">{label}</span>
                <span className="repl-plan-kpi-value">{value}</span>
              </div>
            ))}
          </div>
        </ReportHeader>
        <TableHead />
      </div>
    ),
    [
      companyLogoSrc,
      companyName,
      counts.candidate,
      counts.completed,
      counts.confirmed,
      counts.needed,
      counts.open,
      counts.travel,
      flag,
      lines.length,
      needsReliefOnly,
      vessel?.callSign,
      vessel?.imoNumber,
      vesselName,
      withinDays,
    ],
  );

  const continueHeader = useMemo(
    () => (
      <div className="repl-plan-flow-header repl-plan-flow-continue">
        <div className="repl-plan-continue-bar">
          <span className="repl-plan-continue-title">Replacement plan</span>
          <span className="repl-plan-continue-vessel">{vesselName}</span>
          <span className="repl-plan-continue-hint">continued</span>
        </div>
        <TableHead />
      </div>
    ),
    [vesselName],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportSigns>
          <ReportSignLine label="Crew manager" />
          <ReportSignLine label="Date" />
        </ReportSigns>
        {companyName ? <ReportNote>Issued by {companyName}</ReportNote> : null}
      </ReportFooter>
    ),
    [companyName],
  );

  const { items, itemKeys } = useMemo(() => {
    if (lines.length === 0) {
      return {
        items: [
          <table key="empty" className="repl-plan-table repl-plan-row-table">
            <tbody>
              <tr>
                <td colSpan={9} className="repl-plan-empty">
                  No replacement lines for this vessel and filter.
                </td>
              </tr>
            </tbody>
          </table>,
        ],
        itemKeys: ['empty'],
      };
    }
    return {
      items: lines.map((line, i) => <LineRow key={line.key} line={line} index={i} />),
      itemKeys: lines.map((line) => line.key),
    };
  }, [lines]);

  return (
    <ReportFlowPages
      className="crew-list-print-replacement"
      size="landscape"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default ReplacementPlanReport;
